import React, { useState } from "react";
import { siteInfo } from "../mock";
import { MapPin, Phone, Mail, Facebook } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../lib/api";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactSection() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast({
        title: "Please fill in your name and email",
        description: "We\u2019d love to know who\u2019s reaching out!",
      });
      return;
    }
    if (!emailRegex.test(form.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
      });
      return;
    }
    setSubmitting(true);
    try {
      // Strip empty optional fields so backend EmailStr/length validators don't reject
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v && v.toString().trim() !== "")
      );
      await submitContact(payload);
      setSubmitted(true);
      toast({
        title: "Thanks for submitting!",
        description: "We\u2019ll get back to you shortly.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        subject: "",
        message: "",
      });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      toast({
        title: "Could not send your message",
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[var(--ka-teal)] text-white">
      <div className="container-ka section grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-4xl md:text-5xl mb-8">
            Send us a Note
          </h2>
          <div className="space-y-5 text-[15px]">
            <div>
              <div className="font-medium text-white">{siteInfo.brand}</div>
            </div>
            <div className="flex gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0" />
              <span className="text-white/85">{siteInfo.address}</span>
            </div>
            <a
              href={`mailto:${siteInfo.email}`}
              className="flex gap-3 hover:text-white text-white/85"
            >
              <Mail size={18} className="mt-0.5 shrink-0" />
              <span>{siteInfo.email}</span>
            </a>
            <a
              href={`tel:${siteInfo.phoneRaw}`}
              className="flex gap-3 hover:text-white text-white/85"
            >
              <Phone size={18} className="mt-0.5 shrink-0" />
              <span>{siteInfo.phone}</span>
            </a>
            <div className="pt-2">
              <a
                href={siteInfo.facebook}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full border border-white/40 inline-flex items-center justify-center hover:bg-white hover:text-[var(--ka-teal)] transition"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <div>
            <label className="ka-label">Name *</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              type="text"
              placeholder="Enter your name"
              className="ka-field"
            />
          </div>
          <div>
            <label className="ka-label">Email *</label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              type="email"
              placeholder="Enter your email"
              className="ka-field"
            />
          </div>
          <div>
            <label className="ka-label">Phone</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              type="tel"
              placeholder="Enter your phone number"
              className="ka-field"
            />
          </div>
          <div>
            <label className="ka-label">Address</label>
            <input
              name="address"
              value={form.address}
              onChange={handleChange}
              type="text"
              placeholder="Enter your address"
              className="ka-field"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="ka-label">Subject</label>
            <input
              name="subject"
              value={form.subject}
              onChange={handleChange}
              type="text"
              placeholder="What is this about?"
              className="ka-field"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="ka-label">Message</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={3}
              placeholder="Tell us how we can help"
              className="ka-field resize-none"
            />
          </div>
          <div className="sm:col-span-2 flex flex-col items-start gap-3 mt-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-white disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Submit"}
            </button>
            {submitted && (
              <p className="text-white/90 text-sm fade-in">
                Thanks for submitting!
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
